import * as THREE from 'three';

import { PointSizeType } from '../shared/defines.js';
import { PointCloudMaterial, PointCloudTree } from '../services/pointcloud-runtime.js';

type ProfileAttributeArray = Float32Array | Uint8Array | Uint16Array | Uint32Array;
type PotreeBufferAttribute = THREE.BufferAttribute & { potree?: { range: [number, number] } };
type ProfileMaterial = PointCloudMaterial & {
  gradientTexture?: unknown;
  visibleNodesTexture?: unknown;
  classificationTexture?: unknown;
  activeAttributeName?: unknown;
  ranges?: unknown;
};

export interface ProfilePointBatch {
  numPoints: number;
  data: Record<string, ProfileAttributeArray>;
  projectedBox?: THREE.Box3;
}

interface ProfileBatch {
  geometry: THREE.BufferGeometry;
  material: PointCloudMaterial;
  sceneNode: THREE.Points;
  geometryNode: { estimatedSpacing: number; geometry: THREE.BufferGeometry };
  getLevel(): number;
}

interface ProfileSourcePointCloud {
  pcoGeometry?: unknown;
  position?: THREE.Vector3;
  material?: unknown;
  root?: {
    sceneNode?: {
      geometry?: {
        attributes?: Record<string, THREE.BufferAttribute>;
      };
    };
  };
  getAttribute?(name: string): unknown;
}

export class ProfileFakeOctree extends PointCloudTree {
  readonly trueOctree: ProfileSourcePointCloud;
  readonly pcoGeometry: unknown;
  readonly points: ProfilePointBatch[] = [];
  readonly visibleNodes: ProfileBatch[] = [];
  readonly material: PointCloudMaterial;
  readonly projectedBox = new THREE.Box3();
  readonly batchSize = 100_000;
  currentBatch: ProfileBatch | null = null;

  constructor(source: unknown) {
    super();

    this.trueOctree = (source ?? {}) as ProfileSourcePointCloud;
    this.pcoGeometry = this.trueOctree.pcoGeometry;
    this.material = new PointCloudMaterial();

    if (this.trueOctree.material instanceof PointCloudMaterial) {
      copyProfileMaterial(this.trueOctree.material, this.material);
    }

    this.material.pointSizeType = PointSizeType.FIXED;
    this.material.size = 3;
    this.material.updateShaderSource();
  }

  getAttribute(name: string): unknown {
    return this.trueOctree.getAttribute?.(name) ?? null;
  }

  dispose(): void {
    for (const node of this.visibleNodes) {
      node.geometry.dispose();
      node.sceneNode.parent?.remove(node.sceneNode);
    }

    this.visibleNodes.length = 0;
    this.points.length = 0;
    this.currentBatch = null;
    this.projectedBox.makeEmpty();
  }

  addPoints(batch: ProfilePointBatch): void {
    if (batch.numPoints === 0) {
      return;
    }

    const mileage = batch.data.mileage as Float32Array | undefined;
    const positions = batch.data.position as Float32Array | undefined;
    if (!mileage || !positions) {
      return;
    }

    if (!this.currentBatch) {
      this.currentBatch = this.createNewBatch(batch);
    }

    this.points.push(batch);

    const updateRange = { start: this.currentBatch.geometry.drawRange.count, count: 0 };
    const projectedBox = new THREE.Box3();
    const worldPosition = new THREE.Vector3();
    const basePosition = this.trueOctree.position ?? new THREE.Vector3();

    for (let index = 0; index < batch.numPoints; index++) {
      if (updateRange.start + updateRange.count >= this.batchSize) {
        this.flush();
        this.currentBatch = this.createNewBatch(batch);
        updateRange.start = 0;
        updateRange.count = 0;
      }

      worldPosition.set(
        positions[3 * index + 0] + basePosition.x,
        positions[3 * index + 1] + basePosition.y,
        positions[3 * index + 2] + basePosition.z,
      );

      const projectedIndex = updateRange.start + updateRange.count;
      const geometry = this.currentBatch.geometry;

      for (const attributeName of Object.keys(batch.data)) {
        const sourceAttribute = batch.data[attributeName];
        const targetAttribute = geometry.attributes[attributeName];
        if (!(targetAttribute instanceof THREE.BufferAttribute)) {
          continue;
        }

        const elementsPerPoint = targetAttribute.itemSize;
        for (let elementIndex = 0; elementIndex < elementsPerPoint; elementIndex++) {
          targetAttribute.array[elementsPerPoint * projectedIndex + elementIndex] =
            sourceAttribute[elementsPerPoint * index + elementIndex];
        }
      }

      const profilePosition = geometry.attributes.position as THREE.BufferAttribute;
      profilePosition.array[3 * projectedIndex + 0] = mileage[index];
      profilePosition.array[3 * projectedIndex + 1] = 0;
      profilePosition.array[3 * projectedIndex + 2] = worldPosition.z;

      projectedBox.expandByPoint(new THREE.Vector3(mileage[index], 0, worldPosition.z));
      updateRange.count++;
      this.currentBatch.geometry.drawRange.count++;
    }

    this.flush();
    batch.projectedBox = projectedBox;
    this.projectedBox.copy(
      this.points.reduce((union, pointBatch) => {
        if (pointBatch.projectedBox) {
          union.union(pointBatch.projectedBox);
        }
        return union;
      }, new THREE.Box3()),
    );
  }

  flush(): void {
    if (!this.currentBatch) {
      return;
    }

    const fullCount = this.currentBatch.geometry.drawRange.count;
    for (const attribute of Object.values(this.currentBatch.geometry.attributes)) {
      if (!(attribute instanceof THREE.BufferAttribute)) {
        continue;
      }

      attribute.clearUpdateRanges();
      attribute.addUpdateRange(0, fullCount * attribute.itemSize);
      attribute.needsUpdate = true;
    }

    this.currentBatch.geometry.computeBoundingBox();
    this.currentBatch.geometry.computeBoundingSphere();
  }

  computeVisibilityTextureData(): { data: Uint8Array; offsets: Map<unknown, unknown> } {
    const data = new Uint8Array(this.visibleNodes.length * 4);
    const offsets = new Map();
    for (let index = 0; index < this.visibleNodes.length; index++) {
      offsets.set(this.visibleNodes[index], index);
    }
    return { data, offsets };
  }

  private createNewBatch(batch: ProfilePointBatch): ProfileBatch {
    const geometry = new THREE.BufferGeometry();

    for (const attributeName of Object.keys(batch.data)) {
      const source = batch.data[attributeName];
      const elementsPerPoint = source.length / batch.numPoints;
      const normalized =
        this.trueOctree.root?.sceneNode?.geometry?.attributes?.[attributeName]?.normalized ?? false;

      let batchBuffer: ArrayBufferView;
      if (source instanceof Float32Array)
        batchBuffer = new Float32Array(elementsPerPoint * this.batchSize);
      else if (source instanceof Uint8Array)
        batchBuffer = new Uint8Array(elementsPerPoint * this.batchSize);
      else if (source instanceof Uint16Array)
        batchBuffer = new Uint16Array(elementsPerPoint * this.batchSize);
      else if (source instanceof Uint32Array)
        batchBuffer = new Uint32Array(elementsPerPoint * this.batchSize);
      else batchBuffer = new Float32Array(elementsPerPoint * this.batchSize);

      const attribute = new THREE.BufferAttribute(
        batchBuffer as any,
        elementsPerPoint,
        normalized,
      ) as PotreeBufferAttribute;
      attribute.potree = { range: [0, 1] };
      geometry.setAttribute(attributeName, attribute);
    }

    geometry.drawRange.start = 0;
    geometry.drawRange.count = 0;

    const profileBatch: ProfileBatch = {
      geometry,
      material: this.material,
      sceneNode: new THREE.Points(geometry, this.material as unknown as THREE.Material),
      geometryNode: { estimatedSpacing: 1.0, geometry },
      getLevel: () => 0,
    };

    profileBatch.sceneNode.name = `profile_batch_${this.visibleNodes.length}`;
    this.visibleNodes.push(profileBatch);
    this.add(profileBatch.sceneNode);
    return profileBatch;
  }
}

export function copyProfileMaterial(source: PointCloudMaterial, target: PointCloudMaterial): void {
  const typedSource = source as ProfileMaterial;
  const typedTarget = target as ProfileMaterial;

  for (const key of Object.keys(target.uniforms)) {
    if (source.uniforms[key]) {
      target.uniforms[key].value = source.uniforms[key].value;
    }
  }

  typedTarget.gradientTexture = typedSource.gradientTexture;
  typedTarget.visibleNodesTexture = typedSource.visibleNodesTexture;
  typedTarget.classificationTexture = typedSource.classificationTexture;
  typedTarget.activeAttributeName = typedSource.activeAttributeName;
  typedTarget.ranges = typedSource.ranges;
}

export function syncProfileMaterial(source: unknown, fakeOctree: ProfileFakeOctree): void {
  if (
    !(source instanceof PointCloudMaterial) ||
    !(fakeOctree.material instanceof PointCloudMaterial)
  ) {
    return;
  }

  copyProfileMaterial(source, fakeOctree.material);

  if (!fakeOctree.projectedBox.isEmpty()) {
    const size = fakeOctree.projectedBox.getSize(new THREE.Vector3());
    fakeOctree.material.uniforms.bbSize.value = [
      Math.max(size.x, 1e-3),
      Math.max(size.y, 1e-3),
      Math.max(size.z, 1e-3),
    ];
    fakeOctree.material.uniforms.spacing.value = 1.0;
    if (fakeOctree.material.uniforms.octreeSize) {
      fakeOctree.material.uniforms.octreeSize.value = Math.max(size.x, size.z);
    }
  }

  fakeOctree.material.pointSizeType = PointSizeType.FIXED;
  fakeOctree.material.size = 2;
}

export function countProfilePoints(pointclouds: Iterable<ProfileFakeOctree>): number {
  let total = 0;
  for (const pointcloud of pointclouds) {
    for (const points of pointcloud.points) {
      total += points.numPoints;
    }
  }
  return total;
}
